import { Input } from '@/components/ui/Input';
import { filterMoneyInput } from '@finvox/shared/digits';
import { formatMoneyDisplay, parseMoneyInput, toMoney } from '@finvox/shared/money';
import { cn } from '@/lib/cn';
import { type InputHTMLAttributes, forwardRef, useEffect, useState } from 'react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: string;
  onValueChange: (value: string) => void;
  allowEmpty?: boolean;
};

export const MoneyInput = forwardRef<HTMLInputElement, Props>(function MoneyInput(
  {
    value,
    onValueChange,
    allowEmpty,
    className,
    inputMode = 'decimal',
    onFocus,
    onBlur,
    readOnly,
    disabled,
    ...props
  },
  ref
) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  const displayValue = focused
    ? draft
    : value
      ? (() => {
          try {
            return formatMoneyDisplay(parseMoneyInput(value));
          } catch {
            return value;
          }
        })()
      : '';

  return (
    <Input
      {...props}
      ref={ref}
      type="text"
      inputMode={inputMode}
      autoComplete="off"
      readOnly={readOnly}
      disabled={disabled}
      className={cn(
        'text-end tabular-nums shadow-[var(--fv-shadow-sm)]',
        className
      )}
      value={displayValue}
      onFocus={(e) => {
        if (readOnly || disabled) {
          onFocus?.(e);
          return;
        }
        setFocused(true);
        const zeroLike =
          allowEmpty && value !== '' && toMoney(parseMoneyInput(value)).isZero();
        const initial = zeroLike ? '' : value;
        setDraft(initial);
        if (zeroLike) onValueChange('');
        if (initial) {
          const inputEl = e.currentTarget;
          requestAnimationFrame(() => inputEl.select());
        }
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        const next = filterMoneyInput(e.currentTarget.value);
        setDraft(next);
        if (allowEmpty || next !== '') {
          onValueChange(next);
        } else {
          onValueChange('');
        }
        onBlur?.(e);
      }}
      onInput={(e) => {
        if (readOnly || disabled) return;
        const next = filterMoneyInput(e.currentTarget.value);
        setDraft(next);
        if (allowEmpty || next !== '') {
          onValueChange(next);
        } else {
          onValueChange('');
        }
      }}
      onChange={(e) => {
        if (readOnly || disabled) return;
        const next = filterMoneyInput(e.target.value);
        setDraft(next);
        if (allowEmpty || next !== '') {
          onValueChange(next);
        } else {
          onValueChange('');
        }
      }}
    />
  );
});
